import React, { useEffect, useState } from 'react';
import { PlayerStats } from '../models/PlayerStats';
import { getHeadToHeadMatches } from '../services/MatchService';
import { Link } from 'react-router-dom';
import PlayerImage from '../components/PlayerImage';
import { Match } from '../models/Match';
import './HeadToHead.css';
import { HeadToHeadStats } from '../models/HeadToHeadStats';
import { parseMatchTime } from '../utils/matchTime';

interface HeadToHeadSectionProps {
  player1: PlayerStats;
  player2: PlayerStats;
  isDark: boolean;
}

const HeadToHeadSection: React.FC<HeadToHeadSectionProps> = ({ player1, player2, isDark }) => {
  const [headToHeadData, setHeadToHeadData] = useState<HeadToHeadStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchHeadToHeadData = async () => {
      try {
        setIsLoading(true);
        const data = await getHeadToHeadMatches(player1.steamID, player2.steamID);
        setHeadToHeadData(data);
        setError(null);
      } catch (err) {
        setError('Kunne ikke hente head-to-head statistik');
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchHeadToHeadData();
  }, [player1.steamID, player2.steamID]);

  if (isLoading) {
    return (
      <div className="head-to-head-loading text-center my-4">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Indlæser...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`alert alert-danger my-4 ${isDark ? 'text-light border-secondary' : ''}`}>
        {error}
      </div>
    );
  }

  if (!headToHeadData || headToHeadData.headToHead.totalMatches === 0) {
    return (
      <div className={`head-to-head-empty text-center p-4 my-4 ${isDark ? 'text-light' : 'bg-light'}`}>
        <h4>Ingen kampe fundet mellem disse spillere</h4>
        <p>De har ikke mødt hinanden i registrerede kampe endnu.</p>
      </div>
    );
  }

  const { totalMatches, player1Wins, player2Wins } = headToHeadData.headToHead;
  const player1WinPercentage = (player1Wins / totalMatches) * 100;
  const player2WinPercentage = (player2Wins / totalMatches) * 100;
  
  // Get player names
  const player1Name = player1.account ? player1.account.playerName : player1.steamID;
  const player2Name = player2.account ? player2.account.playerName : player2.steamID;

  return (
    <div className={`head-to-head-section my-4 ${isDark ? 'text-light' : ''}`}>
      <h3 className="text-center mb-4">Head-to-Head Sammenligning</h3>
      
      <div className="head-to-head-summary">
        <div className="row align-items-center mb-4">
          <div className="col-4 text-center">
            <PlayerImage id={player1.steamID} width={80} />
            <h5>{player1Name}</h5>
            <div className="wins">{player1Wins} sejre</div>
          </div>
          
          <div className="col-4 text-center">
            <div className="versus-badge p-2 rounded-pill">VS</div>
            <div className="match-count mt-2">{totalMatches} kampe</div>
          </div>
          
          <div className="col-4 text-center">
            <PlayerImage id={player2.steamID} width={80} />
            <h5>{player2Name}</h5>
            <div className="wins">{player2Wins} sejre</div>
          </div>
        </div>
        
        <div className="win-percentage-bar position-relative mb-4">
          <div 
            className="position-absolute top-0 start-0 h-100" 
            style={{ 
              width: `${player1WinPercentage}%`, 
              backgroundColor: '#007bff',
              borderRadius: player1WinPercentage === 100 ? '0.25rem' : '0.25rem 0 0 0.25rem'
            }}
          ></div>
          <div 
            className="position-absolute top-0 end-0 h-100" 
            style={{ 
              width: `${player2WinPercentage}%`, 
              backgroundColor: '#dc3545',
              borderRadius: player2WinPercentage === 100 ? '0.25rem' : '0 0.25rem 0.25rem 0'
            }}
          ></div>
          <div className="position-relative p-2 text-center">
            <span 
              className={player1WinPercentage > 40 ? 'text-white' : ''}
              style={{ marginRight: '20px' }}
            >
              {player1WinPercentage.toFixed(1)}%
            </span>
            <span 
              className={player2WinPercentage > 40 ? 'text-white' : ''}
              style={{ marginLeft: '20px' }}
            >
              {player2WinPercentage.toFixed(1)}%
            </span>
          </div>
        </div>
      </div>

      <h4 className="mt-4 mb-3">Seneste Kampe</h4>
      <div className="match-history">
        {headToHeadData.matches.slice(0, 5).map((match: Match, index: number) => {
          const player1OnTeam1 = match.team1playerids.includes(player1.steamID);
          const player1Won = (player1OnTeam1 && match.team1won) || (!player1OnTeam1 && !match.team1won);

          // Get scores based on player positions
          const player1Score = player1OnTeam1 ? match.team1score : match.team2score;
          const player2Score = player1OnTeam1 ? match.team2score : match.team1score;

          // Format map name (remove de_ prefix if present)
          const mapDisplayName = match.mapName?.replace(/^de_/i, '') || 'Unknown';

          return (
            <div
              key={index}
              className={`match-history-row ${player1Won ? 'winner-p1' : 'winner-p2'}`}
            >
              <div className="match-row-date">
                {parseMatchTime(match.match_time).toLocaleDateString('da-DK', {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric'
                })}
              </div>

              <div className="match-row-map">{mapDisplayName}</div>

              <div className="match-row-teams">
                <div className="match-row-player">
                  <div className="match-row-player-name">{player1Name}</div>
                  <div className={`match-row-player-result ${player1Won ? 'winner' : 'loser'}`}>
                    {player1Won ? 'Vinder' : 'Taber'}
                  </div>
                </div>

                <div className="match-row-score">{player1Score} - {player2Score}</div>

                <div className="match-row-player">
                  <div className="match-row-player-name">{player2Name}</div>
                  <div className={`match-row-player-result ${!player1Won ? 'winner' : 'loser'}`}>
                    {!player1Won ? 'Vinder' : 'Taber'}
                  </div>
                </div>
              </div>

              <div className="match-row-action">
                <Link
                  to={`/match/${match.matchid}`}
                  className="btn btn-sm btn-primary"
                >
                  Se kamp
                </Link>
              </div>
            </div>
          );
        })}

        {headToHeadData.matches.length > 5 && (
          <div className="text-center mt-3">
            <button className="btn btn-primary">
              Vis alle {headToHeadData.matches.length} kampe
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default HeadToHeadSection;